import React, { useEffect, useMemo, useRef, useState } from 'react';
import clsx from 'clsx';
import { ColorField } from '@/components/inspector-fields/ColorField/ColorField';
import StylesBgColorSettings from '@/components/inspector-fields/ColorField/StylesBgColorSettings';
import type { Color, VisibilityState, Breakpoint } from '@builder4/shared/types/layout';
import { SliderField } from '@/components/inspector-fields/SliderField/SliderField';
import { Visibility } from '@/components/inspector-fields/Visibility/Visibility';
import StylesFontWeightSettings from '@/components/dropdowns/SelectField/StylesFontWeightSettings';
import type { HeadingLevel } from '@builder4/shared/types/styles';
import { isColorReference, processColorUpdate } from '@builder4/shared/utils/styleHelpers';
import { useColor } from '@/hooks/useColor';
import { useAppSelector } from '@/store/hooks';
import { selectEditingStyleColors } from '@/store/globalStyles/selectors';
import { createPaletteFromGlobalColors } from '@/utils/colorPaletteHelpers';

type HeadingType = 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';

interface StylesInspectorHeadingProps {
  className: string;
  onClick?: () => void;
  variant: HeadingType;
  onSizeChange?: (value: number) => void;
  currentData: HeadingLevel;
  onUpdate: (data: HeadingLevel) => void;
}

export const StylesInspectorHeading: React.FC<StylesInspectorHeadingProps> = ({
  className,
  onClick,
  variant,
  onSizeChange,
  currentData: headingData,
  onUpdate,
}) => {
  const [headingFromAttr, setHeadingFromAttr] = useState<HeadingType | undefined>(variant);

  const [selectedFontFamily] = useState<string>('Inter');

  const [selectedDevice, setSelectedDevice] = useState<Breakpoint>('desktop');
  const rootRef = useRef<HTMLDivElement | null>(null);

  const lineHeight = headingData.lineHeight;
  const currentWeight = headingData.fontWeight;
  const currentLetterSpacing = headingData.letterSpacing;

  const styleColors = useAppSelector(selectEditingStyleColors);
  const palettesForPicker = useMemo(
    () => (styleColors ? [createPaletteFromGlobalColors(styleColors)] : []),
    [styleColors],
  );

  const fontColor = useColor(headingData.fontColor, styleColors ?? undefined);
  const backgroundColor = useColor(headingData.backgroundColor, styleColors ?? undefined);

  // Get responsive sizes from currentData
  const responsiveHeadingSizes = useMemo(() => {
    const headingKey = (variant || headingFromAttr) as HeadingType;
    if (headingKey && headingData.desktop && headingData.tablet && headingData.mobile) {
      return {
        desktop: headingData.desktop.fontSize,
        tablet: headingData.tablet.fontSize,
        mobile: headingData.mobile.fontSize,
      };
    }

    return null;
  }, [headingData, variant, headingFromAttr]);

  const [visibility, setVisibility] = useState<VisibilityState | undefined>(undefined);

  const handleClick = () => onClick?.();

  const handleOnLineHeightChange = (value: number) => {
    onUpdate({ ...headingData, lineHeight: value });
  };

  const handleVisibilityChange = (devices: Breakpoint[]) => {
    if (variant || headingFromAttr) {
      setSelectedDevice(devices[0] ?? 'desktop');
      return;
    }
    setVisibility({
      desktop: devices.includes('desktop'),
      tablet: devices.includes('tablet'),
      mobile: devices.includes('mobile'),
    });
  };

  const handleFontSizeChange = (val: number) => {
    onSizeChange?.(val);
    if (variant || headingFromAttr) {
      const deviceKey = selectedDevice;
      onUpdate({
        ...headingData,
        [deviceKey]: { fontSize: val },
      });
    }
  };

  useEffect(() => {
    if (variant) {
      setHeadingFromAttr(variant);
      return;
    }
    const attr = rootRef.current?.getAttribute('data-heading') as HeadingType | null;
    if (attr) setHeadingFromAttr(attr);
  }, [variant]);

  const fieldWidthClass = 'w-full';

  const selectedDevices: Breakpoint[] = useMemo(() => {
    if (headingFromAttr) return [selectedDevice];
    const v = visibility as VisibilityState | undefined;
    if (!v) return [];
    const arr: Breakpoint[] = [];
    if (v.desktop) arr.push('desktop');
    if (v.tablet) arr.push('tablet');
    if (v.mobile) arr.push('mobile');
    return arr;
  }, [visibility, headingFromAttr, selectedDevice]);

  const handleWeightChange = (weight: string) => {
    onUpdate({ ...headingData, fontWeight: weight });
  };

  const handleLetterSpacingChange = (spacing: number) => {
    onUpdate({ ...headingData, letterSpacing: spacing });
  };

  const handleColorChange = (value: Color | string) => {
    if (typeof value === 'string' && isColorReference(value)) {
      onUpdate({ ...headingData, fontColor: value });
      return;
    }
    if (typeof value === 'string') return;
    const processed = processColorUpdate(value);
    if (processed === undefined || processed === null) return;
    onUpdate({ ...headingData, fontColor: processed });
  };

  const handleBgColorChange = (value: Color | string | null | undefined) => {
    if (value === null || value === undefined) {
      onUpdate({ ...headingData, backgroundColor: null });
      return;
    }
    if (typeof value === 'string' && isColorReference(value)) {
      onUpdate({ ...headingData, backgroundColor: value });
      return;
    }
    if (typeof value === 'string') return;
    const processed = processColorUpdate(value);
    if (processed === undefined) return;
    onUpdate({ ...headingData, backgroundColor: processed });
  };

  return (
    <div
      onClick={handleClick}
      style={{ width: 248 }}
      data-heading={variant}
      ref={rootRef}
      className={clsx('flex flex-col gap-3', className)}
    >
      <div className="flex flex-col gap-3 p-3">
        {/* Heading mode: Responsive sizes controls */}

        <>
          <p className="pb-1 text-xs font-semibold text-neutral-900">Responsive sizes</p>
          <Visibility
            selected={selectedDevices}
            onChange={handleVisibilityChange}
            text="Select screen"
            className=""
            inactiveUseOutline={true}
            selectionMode="single"
          />
        </>

        {/* Text Size using base SliderField */}
        <SliderField
          key={`${headingFromAttr ?? 'text'}-${selectedDevice}`}
          label="Text Size"
          value={responsiveHeadingSizes?.[selectedDevice]}
          onChange={handleFontSizeChange}
          min={0}
          max={100}
          suffix="px"
        />

        <div className="my-1 h-0 self-stretch border-t border-neutral-100" />

        <p className="pb-1 text-xs font-semibold text-neutral-900">Text</p>

        <StylesFontWeightSettings
          className={'w-full'}
          value={currentWeight}
          fontFamily={selectedFontFamily}
          onChange={handleWeightChange}
        />

        {/* Inline NodeColorSettings UI */}
        <ColorField
          hasGradient={true}
          canClear={false}
          className={fieldWidthClass}
          inputValue={fontColor.hexForInput}
          placeholderText="-"
          state="default"
          text="Color"
          type="text"
          color={fontColor.resolvedColor || undefined}
          gradient={fontColor.gradient}
          mode={fontColor.mode}
          palettes={palettesForPicker}
          onChange={handleColorChange}
          onChangeComplete={handleColorChange}
        />
        <StylesBgColorSettings
          className={fieldWidthClass}
          text="Background Color"
          value={backgroundColor.resolvedColor}
          palettes={palettesForPicker}
          onChange={handleBgColorChange}
          onChangeComplete={handleBgColorChange}
        />
        <SliderField
          className="w-full"
          label="Line Height"
          value={lineHeight || 0}
          onChange={handleOnLineHeightChange}
          min={0}
          max={5}
          allowDecimal={true}
        />
        {/* Inline NodeSpaceSettings UI */}
        <SliderField
          label="Spacing"
          value={currentLetterSpacing || 0}
          onChange={handleLetterSpacingChange}
          onChangeComplete={handleLetterSpacingChange}
          min={-2}
          max={2}
          step={0.01}
          allowDecimal={true}
        />
      </div>
    </div>
  );
};

export default StylesInspectorHeading;
